// @ts-check
/**
 * Every GST tax invoice by date, with a one-click "Export for CA" (ZIP of the PDFs + a summary.csv).
 * Invoices are raised on the order day, so partially paid orders appear here too.
 */
import { ChevronLeft, ChevronRight, Download, ExternalLink, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../context/ToastContext';
import adminInvoiceService from '../services/adminInvoiceService';

const PAGE_SIZE = 50;
const input = 'px-3 py-2 text-base border border-input rounded-lg';
const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;
const isoDay = (d) => d.toISOString().slice(0, 10);

/** This month so far; the page's date pickers cover any other range (e.g. last month for the CA). */
const thisMonth = () => {
  const today = isoDay(new Date());
  return { from: `${today.slice(0, 8)}01`, to: today };
};

const InvoicesPage = () => {
  const toast = useToast();
  const [range, setRange] = useState(thisMonth);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminInvoiceService.list(range, page, PAGE_SIZE));
    } catch (err) {
      toast.error(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    if (!range.from || !range.to) return toast.error('Choose both dates');
    setExporting(true);
    try {
      const blob = await adminInvoiceService.exportZip(range);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `kaay-invoices-${range.from}-to-${range.to}.zip`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error(err.message || 'Could not export the invoices');
    } finally {
      setExporting(false);
    }
  };

  const set = (field, value) => {
    setPage(1);
    setRange((prev) => ({ ...prev, [field]: value }));
  };
  const pageCount = Math.max(1, Math.ceil((data?.count ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Invoices</h1>
          <p className="text-sm text-muted-foreground">
            Raised on the order day, even when part of the payment is still due.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">From</label>
            <input type="date" className={input} value={range.from} onChange={(e) => set('from', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">To</label>
            <input type="date" className={input} value={range.to} onChange={(e) => set('to', e.target.value)} />
          </div>
          <button type="button" onClick={load} className="p-2 border rounded-lg" aria-label="Refresh">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !data?.count}
            className="flex items-center gap-2 px-4 py-2 text-base rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
          >
            <Download size={18} />
            {exporting ? 'Preparing…' : 'Export for CA'}
          </button>
        </div>
      </div>

      {data && (
        <div className="text-sm text-muted-foreground">
          {data.count} invoice{data.count === 1 ? '' : 's'} · taxable {inr(data.totals.taxable_value)} · CGST{' '}
          {inr(data.totals.cgst)} · SGST {inr(data.totals.sgst)} · IGST {inr(data.totals.igst)} · total{' '}
          {inr(data.totals.total)}
          {data.cancelled_count > 0 && ` (excludes ${data.cancelled_count} cancelled)`}
        </div>
      )}

      <div className="border rounded-lg border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="px-3 py-2">Invoice</th>
              <th className="px-3 py-2">Date</th>
              <th className="px-3 py-2">Order</th>
              <th className="px-3 py-2">Customer</th>
              <th className="px-3 py-2">Tax</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2">Payment</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {(data?.invoices ?? []).map((inv) => (
              <tr
                key={inv.id}
                className={`border-t border-border ${inv.invoice_status === 'cancelled' ? 'text-muted-foreground' : ''}`}
              >
                <td className="px-3 py-2 font-medium">
                  {inv.invoice_number}
                  {inv.invoice_status === 'cancelled' && (
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">Cancelled</span>
                  )}
                </td>
                <td className="px-3 py-2">{inv.invoice_date}</td>
                <td className="px-3 py-2">{inv.order_number}</td>
                <td className="px-3 py-2">{inv.customer}</td>
                <td className="px-3 py-2">{inv.tax_type}</td>
                <td className="px-3 py-2 text-right">{inr(inv.total)}</td>
                <td className="px-3 py-2">{inv.payment_status.replace('_', ' ')}</td>
                <td className="px-3 py-2">
                  {inv.pdf_url && (
                    <a href={inv.pdf_url} target="_blank" rel="noreferrer" aria-label="Open PDF">
                      <ExternalLink size={16} />
                    </a>
                  )}
                </td>
              </tr>
            ))}
            {!loading && data?.count === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">
                  No invoices in this date range
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <button
            type="button"
            className="p-2 border rounded-lg disabled:opacity-50"
            disabled={page <= 1 || loading}
            onClick={() => setPage(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft size={16} />
          </button>
          <span>
            Page {page} of {pageCount}
          </span>
          <button
            type="button"
            className="p-2 border rounded-lg disabled:opacity-50"
            disabled={page >= pageCount || loading}
            onClick={() => setPage(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
